<template lang="pug">
.acting-entity(@keydown.esc="close")
  //- Clear layer under the open list: a click anywhere else only closes it
  .acting-entity-backdrop(v-if="open" data-testid="acting-entity-backdrop" @click="close")
  button.acting-entity-card(
    type="button"
    :class="{ 'is-switchable': canSwitch, 'is-open': open }"
    :disabled="!canSwitch"
    :aria-label="canSwitch ? `Acting as ${currentName}. Switch account` : currentName"
    :aria-expanded="canSwitch ? String(open) : undefined"
    aria-controls="acting-entity-list"
    data-testid="acting-entity-card"
    @click="toggle"
  )
    .acting-entity-avatar
      organization-avatar(
        :name="currentName || '?'"
        :avatarUrl="currentAvatar"
        :size="44"
        :borderWidth="2"
        borderColor="rgba(255, 255, 255, 0.85)"
      )
    .acting-entity-text
      .acting-entity-name(:title="currentName") {{ currentName }}
      .acting-entity-kind {{ currentKind }}
    fai.acting-entity-chevron(v-if="canSwitch" type="fal" icon="chevron-down")

  //- Floats over the menu items below the card
  .acting-entity-list(
    v-if="open"
    id="acting-entity-list"
    data-testid="acting-entity-list"
  )
    button.acting-entity-row(
      type="button"
      :class="{ 'is-selected': !isOrganization }"
      :aria-current="!isOrganization ? 'true' : undefined"
      @click="choose(null)"
    )
      organization-avatar(
        :name="userName || '?'"
        :avatarUrl="userAvatar"
        :size="32"
        :borderWidth="0"
      )
      .acting-entity-row-text
        .acting-entity-row-name(:title="userName") {{ userName }}
        .acting-entity-row-kind Personal account
      fai.acting-entity-check(v-if="!isOrganization" type="fal" icon="check")
    button.acting-entity-row(
      v-for="org in organizations"
      :key="org.id"
      type="button"
      :class="{ 'is-selected': organization?.id === org.id }"
      :aria-current="organization?.id === org.id ? 'true' : undefined"
      @click="choose(org)"
    )
      organization-avatar(
        :name="org.name || '?'"
        :avatarUrl="org.avatar"
        :size="32"
        :borderWidth="0"
      )
      .acting-entity-row-text
        .acting-entity-row-name(:title="org.name") {{ org.name }}
        .acting-entity-row-kind {{ organizationKind(org) }}
      fai.acting-entity-check(v-if="organization?.id === org.id" type="fal" icon="check")
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';

import OrganizationAvatar from '@/views/web-app/glow/components/OrganizationAvatar.vue';
import {
  actingProfilePath, organizationKind, useActingEntity,
} from '@/composables/useActingEntity';
import type { ActingOrganization } from '@/store/modules/actingEntity';

export default defineComponent({
  name: 'ActingEntitySwitcher',
  components: { OrganizationAvatar },

  props: {
    /** `profile` from getProfile: the user's own avatar */
    profile: { type: Object, default: null },
  },

  setup(props) {
    const store = useStore();
    const router = useRouter();
    const {
      organization, organizations, isOrganization, selectOrganization, loadOrganizations,
    } = useActingEntity();

    const open = ref(false);
    const me = computed(() => store.getters['users/me']);

    // A name saved in Settings updates `me`; it wins over the profile this menu loaded earlier
    const renamed = ref(false);
    watch(() => [me.value?.firstname, me.value?.lastname].join('\u0000'), () => {
      renamed.value = true;
    });

    const userName = computed(() => {
      const user = (renamed.value ? me.value : null) ?? props.profile?.user ?? me.value;
      return [user?.firstname, user?.lastname].filter(Boolean).join(' ').trim()
        || user?.email || 'My account';
    });
    const userAvatar = computed(() => props.profile?.avatar ?? null);

    const currentName = computed(() => organization.value?.name || userName.value);
    const currentAvatar = computed(() => (
      organization.value ? organization.value.avatar ?? null : userAvatar.value
    ));
    const currentKind = computed(() => (
      organization.value ? organizationKind(organization.value) : 'Personal account'
    ));
    // Like the mobile drawer: only users with an approved organization can switch
    const canSwitch = computed(() => organizations.value.length > 0);

    const close = () => {
      open.value = false;
    };
    const toggle = () => {
      if (canSwitch.value) open.value = !open.value;
    };

    const choose = async (org: ActingOrganization | null) => {
      close();
      if ((org?.id ?? null) === (organization.value?.id ?? null)) return;
      await selectOrganization(org);
      ElMessage.success(org ? `You're now acting as ${org.name}` : 'Switched to your personal account');
      router.push(actingProfilePath(org, me.value));
    };

    onMounted(async () => {
      try {
        await loadOrganizations();
      } catch (error) {
        // the card still shows the current account; switching stays unavailable
        console.error('Error loading organizations to switch to:', error);
      }
    });

    return {
      organization,
      organizations,
      isOrganization,
      open,
      userName,
      userAvatar,
      currentName,
      currentAvatar,
      currentKind,
      canSwitch,
      toggle,
      close,
      choose,
      organizationKind,
    };
  },
});
</script>

<style lang="scss" scoped>
.acting-entity {
  position: relative;
  margin: 0 16px 16px;
}

.acting-entity-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10;
  background: transparent;
}

.acting-entity-card {
  position: relative;
  z-index: 11;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  text-align: left;
  cursor: default;

  &.is-switchable {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover,
    &:focus-visible {
      background: rgba(255, 255, 255, 0.12);
    }
  }

  &.is-open {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    background: #2d6a79;

    .acting-entity-chevron {
      transform: rotate(180deg);
    }
  }

  &:disabled {
    opacity: 1;
  }
}

.acting-entity-avatar {
  flex-shrink: 0;
}

.acting-entity-text {
  flex: 1;
  min-width: 0;
}

.acting-entity-name {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.acting-entity-kind {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.acting-entity-chevron {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: rgba(255, 255, 255, 0.8);
  transition: transform 0.2s;
}

.acting-entity-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 11;
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-top: 0;
  border-radius: 0 0 14px 14px;
  // Opaque: it covers the menu items underneath
  background: #1b4c59;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35);
}

.acting-entity-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.1);
  }

  &.is-selected .acting-entity-row-name {
    font-weight: 700;
  }
}

.acting-entity-row-text {
  flex: 1;
  min-width: 0;
}

.acting-entity-row-name {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.acting-entity-row-kind {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.acting-entity-check {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: #5eead4;
}
</style>
